
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { Phone, Mail, MapPin, Clock, Facebook, Twitter, Instagram, Linkedin } from "lucide-react";
import logo from "@/assets/logo-new.png";

const Footer = () => {
  const { language, isRTL } = useLanguage();

  const content = {
    ar: {
      about: "مجموعة الفنون للصناعات الحديثة - رواد في تصنيع الألمنيوم، الحديد، الزجاج، الرخام، الستانلس ستيل والأخشاب منذ أكثر من 10 سنوات.",
      quickLinks: "روابط سريعة",
      services: "خدماتنا",
      contactInfo: "معلومات التواصل",
      followUs: "تابعنا",
      rights: "جميع الحقوق محفوظة",
      links: [
        { name: "الرئيسية", path: "/" },
        { name: "من نحن", path: "/about-us" },
        { name: "خدماتنا", path: "/services" },
        { name: "معرض الأعمال", path: "/gallery" },
        { name: "المقالات", path: "/articles" },
        { name: "تواصل معنا", path: "/contact" },
      ],
      serviceLinks: [
        { name: "الألمنيوم", path: "/aluminum" },
        { name: "الحديد", path: "/iron" },
        { name: "الزجاج", path: "/glass" },
        { name: "الرخام", path: "/marble" },
        { name: "الستانلس ستيل", path: "/stainless-steel" },
        { name: "الأخشاب", path: "/wood" },
      ],
      address: "الخبر، المنطقة الشرقية، المملكة العربية السعودية",
      phone: "+966553077391",
      email: "a-sbar@alfunoongroup.com",
      workHours: "السبت - الخميس: 8 صباحاً - 6 مساءً",
    },
    en: {
      about: "ALFUNOON Modern Industries Group - Leaders in aluminum, iron, glass, marble, stainless steel, and wood manufacturing for over 10 years.",
      quickLinks: "Quick Links",
      services: "Our Services",
      contactInfo: "Contact Info",
      followUs: "Follow Us",
      rights: "All Rights Reserved",
      links: [
        { name: "Home", path: "/" },
        { name: "About Us", path: "/about-us" },
        { name: "Services", path: "/services" },
        { name: "Gallery", path: "/gallery" },
        { name: "Articles", path: "/articles" },
        { name: "Contact Us", path: "/contact" },
      ],
      serviceLinks: [
        { name: "Aluminum", path: "/aluminum" },
        { name: "Iron", path: "/iron" },
        { name: "Glass", path: "/glass" },
        { name: "Marble", path: "/marble" },
        { name: "Stainless Steel", path: "/stainless-steel" },
        { name: "Wood", path: "/wood" },
      ],
      address: "Al Khobar, Eastern Province, Saudi Arabia",
      phone: "+966553077391",
      email: "a-sbar@alfunoongroup.com",
      workHours: "Sat - Thu: 8 AM - 6 PM",
    },
  };

  const t = content[language];

  return (
    <footer className="bg-primary text-primary-foreground" dir={isRTL ? "rtl" : "ltr"}>
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About Section */}
          <div className="space-y-4">
            <Link to="/" className="inline-block">
              <img src={logo} alt="ALFUNOON" className="h-16 w-auto brightness-0 invert" />
            </Link>
            <p className="text-primary-foreground/80 font-kufi text-sm leading-relaxed">
              {t.about}
            </p>
            {/* Social Media */}
            <div className="flex gap-3 pt-2">
              <a href="#" className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors">
                <Twitter className="h-5 w-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors">
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold font-kufi mb-4 border-b border-primary-foreground/20 pb-2">
              {t.quickLinks}
            </h3>
            <ul className="space-y-2">
              {t.links.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-primary-foreground/80 hover:text-primary-foreground font-kufi text-sm transition-colors inline-block py-1"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold font-kufi mb-4 border-b border-primary-foreground/20 pb-2">
              {t.services}
            </h3>
            <ul className="space-y-2">
              {t.serviceLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-primary-foreground/80 hover:text-primary-foreground font-kufi text-sm transition-colors inline-block py-1"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold font-kufi mb-4 border-b border-primary-foreground/20 pb-2">
              {t.contactInfo}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <span className="text-primary-foreground/80 font-kufi text-sm">{t.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 flex-shrink-0" />
                <a href={`tel:${t.phone}`} className="text-primary-foreground/80 hover:text-primary-foreground font-kufi text-sm transition-colors">
                  {t.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 flex-shrink-0" />
                <a href={`mailto:${t.email}`} className="text-primary-foreground/80 hover:text-primary-foreground font-kufi text-sm transition-colors">
                  {t.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="h-5 w-5 flex-shrink-0" />
                <span className="text-primary-foreground/80 font-kufi text-sm">{t.workHours}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-2 text-sm text-primary-foreground/70 font-kufi">
            <p>© {new Date().getFullYear()} ALFUNOON. {t.rights}</p>
            <p className="flex items-center gap-1">
              {language === 'ar' ? 'صُنع بـ ❤️ في السعودية' : 'Made with ❤️ in Saudi Arabia'}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
